// AppShell — the persistent Ros shell. This IS the former RosApp, reorganized (not re-architected):
// it keeps ALL app state + handlers verbatim, and renders the rail + an always-mounted ChatWorkspace +
// the router <Outlet/> (route pages: Home, Chats, placeholders). The chat/panel JSX lives in
// ChatWorkspace; the cross-route slice is exposed via RosShellContext.
//
// Why R3Chat is always mounted (not per-route): R3Chat owns its session/messages/streaming state
// internally — unmounting it on navigation would destroy the live conversation. So the workspace stays
// mounted and the router toggles VISIBILITY (the route page overlays it; on /chat the page is empty so
// the workspace shows). liveSessionId/sessions are React state; the URL only REFLECTS them.

import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import R3Chat, { ROS_TURN_CHANNEL } from '@reachinsight-app/shared/src/components/R3Chat/R3Chat'
import type { R3ChatHandle } from '@reachinsight-app/shared/src/components/R3Chat/R3Chat'
import type { RestoredChatMessage } from '@reachinsight-app/shared/src/components/R3Chat/activity'
import type { AppArtifactOpenData, SessionSummary, LocationRequestData, MarkdownArtifactPanelData, McpAppData, SiteArtifacts, SiteBuilderPlanData, SiteBuilderCodeChunkData } from '@reachinsight-app/shared/src/components/R3Chat/types'
import type { McpAppContainerHandle } from '@reachinsight-app/shared/src/components/R3Chat/McpAppContainer'
import { SessionsOverlay } from './components/SessionsOverlay'
import { AgentsScreen } from './components/AgentsScreen'
import { SettingsOverlay } from './components/SettingsOverlay'
import { LocationConsentDialog, type LocationDialogStage } from './components/LocationConsentDialog'
import {
  confirmUserLocation,
  declineBrowserLocation,
  fetchUserSettings,
  patchUserSettings,
  removeUserLocation,
  resolveBrowserLocation,
  resolveManualLocation,
  uploadAvatar,
  type ResolvedLocationResult,
  type UserSettings,
  type UserSettingsPatch,
} from './pages/userSettingsApi'
import { addChatToProject } from './pages/projectsApi'
import { WebChatService } from '@reachinsight-app/shared/src/services/WebChatService'
import { useFeatureFlag } from '@reachinsight-app/shared/src'
import { config, KNOWN_SOURCES } from './config/environment'
import { RosRail } from './components/RosRail'
import { ChatWorkspace } from './components/ChatWorkspace'
import { RosShellContext, type RosShellValue } from './RosShellContext'
import {
  type SessionArtifact, type PanelArtifactVersion,
  latestPublishedAt, artifactTypeToPanelType,
} from './components/rosArtifacts'
import { typeDef } from './pages/artifactsModel'
import { createArtifactChat, deleteArtifact } from './pages/artifactsApi'
import { useMessagesMirrorRef } from './hooks/useChatMessageMirroring'
import { openSandboxedWebsitePreview } from './services/sitePreviewWindow'
import {useRosFeatures} from './access/useRosFeatures'

const SIDEBAR_PREF_KEY = 'ros.sidebarCollapsed'

interface LocationFlow {
  source: 'chat' | 'settings'
  stage: LocationDialogStage
  reason?: string
  result: ResolvedLocationResult | null
  resolutionSource: 'browser' | 'manual'
}

export function AppShell() {
  const {auth, enabled, snapshot, authFetch} = useRosFeatures()
  const navigate = useNavigate()
  const location = useLocation()
  const { sessionId: urlSessionId } = useParams()
  const [devMode, setDevMode] = useState(false)

  // Authenticated UI access comes only from the server-created Ros snapshot.
  const showAgents = enabled('reachy-agent-builder')
  // Registry is an admin/config surface, so it lives as a Settings tab (not a rail nav item), gated
  // by the same ros-registry flag the /registry route uses.
  const showRegistry = enabled('ros-registry')
  // Gates the debug/dev-mode toggle icon (CL-33763): only devs with the flag see it.
  const showDevToggle = snapshot.userType === 'internal' && enabled('ros-dev-mode')
  const showExtensions = useFeatureFlag('ros-extensions')
  const artifactsMgmtEnabled = enabled('artifacts-mgmt')
  const authoringEnabled = enabled('authoring-agent-enabled')
  const crosstabsEnabled = enabled('crosstabs-agent-enabled')
  const websiteBuilderEnabled = enabled('website-builder-agent-enabled')
  const showLocationTool = enabled('ros-tools')
  const [agentsOpen, setAgentsOpen] = useState(false)

  useEffect(() => {
    if (!showAgents) setAgentsOpen(false)
    if (!showDevToggle) setDevMode(false)
  }, [showAgents, showDevToggle])

  // ── Chat ──────────────────────────────────────────────────────────
  const chatRef = useRef<R3ChatHandle>(null)
  // When a chat is started from inside a project, we stash that project id here. The session id only
  // exists once the first message creates it — handleSessionCreated reads this ref then and associates
  // the fresh chat with the project (one-shot; cleared after).
  const pendingProjectRef = useRef<string | null>(null)

  // STABLE instance (not keyed on the token): recreating it on token renewal would orphan the old
  // instance's abort controller mid-SSE-stream. The renewed token is pushed via setAccessToken below.
  const chatService = useMemo(() => {
    if (!auth.ready) return null
    return new WebChatService(config.API_CF_URL, auth.token ?? undefined)
  }, [auth.ready]) // eslint-disable-line react-hooks/exhaustive-deps -- token pushed via effect
  useEffect(() => {
    if (chatService && auth.token) chatService.setAccessToken(auth.token)
  }, [chatService, auth.token])

  // ── Sessions (server-side paginated) ──────────────────────────────
  const SESSIONS_PAGE = 200
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [sessionsHasMore, setSessionsHasMore] = useState(false)
  const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false)
  const [liveSessionId, setLiveSessionId] = useState<string | undefined>()
  // Whether the live chat has any messages — owned here (not in ChatWorkspace) so the rail can read it
  // via the shell context to drive the "Ask Ros" highlight (lit only on a blank, message-less chat).
  const [chatHasMessages, setChatHasMessages] = useState(false)
  // User profile settings (Settings page). Fetched once; the overlay edits it, the rail avatar + Home
  // greeting read it. null until loaded.
  const [userSettings, setUserSettings] = useState<UserSettings | null>(null)
  // Whether the settings fetch has resolved. Greetings hold off on showing a name until this is true,
  // so the name doesn't flicker (JWT name/email flashing, then snapping to the Settings name).
  const [userSettingsLoaded, setUserSettingsLoaded] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [locationBusy, setLocationBusy] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [locationFlow, setLocationFlow] = useState<LocationFlow | null>(null)

  const fetchSessionsPage = useCallback(async (offset: number) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    const res = await authFetch(`${config.API_CF_URL}/api/r3/sessions?limit=${SESSIONS_PAGE}&offset=${offset}`, {
      credentials: 'include', headers,
    })
    if (!res.ok) throw new Error(`sessions ${res.status}`)
    return res.json() as Promise<{ sessions: SessionSummary[]; hasMore?: boolean }>
  }, [authFetch, auth.token])

  const loadSessions = useCallback(async () => {
    try {
      const data = await fetchSessionsPage(0)
      setSessions(data.sessions || [])
      setSessionsHasMore(!!data.hasMore)
    } catch {
      // Session loading is best-effort
    } finally {
      setSessionsLoading(false)
    }
  }, [fetchSessionsPage])

  const loadMoreSessions = useCallback(async () => {
    if (sessionsLoadingMore || !sessionsHasMore) return
    setSessionsLoadingMore(true)
    try {
      const data = await fetchSessionsPage(sessions.length)
      setSessions(prev => [...prev, ...(data.sessions || [])])
      setSessionsHasMore(!!data.hasMore)
    } catch {
      // best-effort
    } finally {
      setSessionsLoadingMore(false)
    }
  }, [fetchSessionsPage, sessions.length, sessionsHasMore, sessionsLoadingMore])

  useEffect(() => {
    if (auth.ready) loadSessions()
  }, [auth.ready, loadSessions])

  // ── Overlays / banners ────────────────────────────────────────────
  const [historyOpen, setHistoryOpen] = useState(false)
  const [draftBanner, setDraftBanner] = useState<{ type: string; baseVersion: number | null } | null>(null)

  // ── Sidebar collapse (persisted; auto-collapses on /chat) ──────────
  // Single source of truth for the rail width. The manual toggle (rail chevron) ALWAYS wins. Entering
  // chat AUTO-collapses it once (see effect below) but does not force it — so the user can expand the
  // rail back even while in chat. Persisted across reloads.
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_PREF_KEY) === 'true' } catch { return false }
  })
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed)
    try { localStorage.setItem(SIDEBAR_PREF_KEY, String(collapsed)) } catch { /* storage unavailable */ }
  }, [])
  // Chat route ONLY: exactly /chat or /chat/:id — NOT /chats (the Chats list page). A naive
  // startsWith('/chat') matches '/chats' too, which would keep the chat workspace visible (hiding the
  // Chats page) and wrongly auto-collapse the rail on the Chats route.
  const inChat = location.pathname === '/chat' || location.pathname.startsWith('/chat/')
  const railCollapsed = sidebarCollapsed

  // Auto-collapse ONCE per transition into chat (matches the chat-mode mock). Fires only on the
  // false→true edge of inChat, so it doesn't fight the manual expand toggle while you're in chat.
  const wasInChatRef = useRef(false)
  useEffect(() => {
    if (inChat && !wasInChatRef.current) setSidebarCollapsedState(true)
    wasInChatRef.current = inChat
  }, [inChat])

  // True while a session's history is being fetched + applied to R3Chat. Drives the loading
  // overlay on the chat column: without it, a hard refresh (or a rail click) shows R3Chat's
  // EMPTY state for the duration of the fetch, which reads as a lost conversation.
  const [sessionResuming, setSessionResuming] = useState(false)
  // Cold-mount variant: the app was OPENED on /chat/:id (hard refresh / direct link), so a resume is
  // inevitable from the very first frame. This drives ONE full-viewport loader (identical to the
  // auth-gate spinner) held from mount until the conversation is applied, so the whole cold load
  // reads as a single continuous loading screen: no auth-spinner to flag-spinner to chat-spinner
  // parade, no empty-chat flash. Lazy init: the URL naming a session is a synchronous render-time
  // signal, whereas effects (and auth.ready) only land after the first paint.
  const [initialChatLoadPending, setInitialChatLoadPending] = useState<boolean>(() => Boolean(urlSessionId))

  // What the chat currently DISPLAYS (role+content mirror from R3Chat via ChatWorkspace) — the
  // quiet re-sync compares fetched history against this and skips the loadSession rebuild when
  // nothing changed. A rebuild is destructive to UX (rows remount, scroll resets), so it must
  // only happen when there is actually something new to show.
  const displayedMessagesRef = useRef<Array<{ role: 'user' | 'assistant'; content: string }>>([])
  // ChatWorkspace's message handler depends on this callback, so its identity must also remain
  // stable across unrelated AppShell renders.
  const handleMessagesMirror = useMessagesMirrorRef(displayedMessagesRef)
  // Whether THIS tab owns a turn in flight — true from the moment of SEND (R3Chat's isStreaming,
  // via onStreamingChange), not from the first streamed token. The quiet re-sync gates on this:
  // rebuilding while our own turn is in flight would CANCEL the live POST stream (loadSession
  // calls chatService.cancel()) and replace the chat with server history that may not contain the
  // just-sent message yet — the message would simply vanish from the UI.
  const chatStreamingRef = useRef(false)
  const pendingLocationContinuationRef = useRef<string | null>(null)

  // Outcome of a select/re-sync attempt — quietResync retries on 'unchanged' (a broadcast promised
  // new content that hasn't hit the server read path yet); everything else ends the attempt.
  type SelectOutcome = 'applied' | 'unchanged' | 'aborted'

  const handleSelectSession = useCallback(async (sessionId: string, opts?: { quiet?: boolean }): Promise<SelectOutcome> => {
    // quiet = a background re-sync (cross-tab broadcast / tab refocus): refresh the messages and
    // reattach to a running turn WITHOUT the loading overlay — the user is already looking at a
    // rendered chat; flashing "Loading conversation" over it would read as a glitch.
    if (!opts?.quiet) setSessionResuming(true)
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`

      // Fetch history FIRST — do NOT gate on chatRef here. On a hard refresh of /chat/:id the resume
      // effect fires the instant auth becomes ready, in the same commit that mounts R3Chat, so
      // chatRef.current is still null at that moment. The old code bailed here and never retried,
      // which is exactly why a browser refresh loaded the panel (plain React state) but an EMPTY chat
      // (the imperative loadSession never landed). Fetching first, then waiting for the handle, fixes it.
      const [msgRes, ctxRes] = await Promise.all([
        authFetch(`${config.API_CF_URL}/api/r3/sessions/${sessionId}/messages`, { credentials: 'include', headers }),
        authFetch(`${config.API_CF_URL}/api/r3/sessions/${sessionId}/context-tokens`, { credentials: 'include', headers }),
      ])
      if (!msgRes.ok) return 'aborted'
      const data = await msgRes.json() as {messages: RestoredChatMessage[]; turn?: {status: 'running' | 'complete' | 'error' | 'interrupted'} | null}
      const contextData = ctxRes.ok ? await ctxRes.json() as { contextTokens: number; contextWindow: number } : null

      // Quiet re-sync guards (in order):
      // 1. Re-check the own-turn gate AFTER the fetch — if this tab started a turn while the
      //    fetch was in flight, applying now would cancel that live stream and eat the message.
      // 2. No-op guard: nothing running and the fetched history matches what's on screen —
      //    applying would only remount every row and reset the scroll. Skip entirely.
      //    (Normalized to role+trimmed content; R3Chat's mirror excludes in-flight streaming rows.)
      if (opts?.quiet) {
        if (chatStreamingRef.current) return 'aborted'
        if (data.turn?.status !== 'running') {
          const norm = (msgs: Array<{ role: string; content: string }>) =>
            msgs.filter(m => m.content?.trim()).map(m => `${m.role}:${m.content.trim()}`)
          const fetched = norm(data.messages || [])
          const shown = norm(displayedMessagesRef.current)
          if (fetched.length === shown.length && fetched.every((v, i) => v === shown[i])) return 'unchanged'
        }
      }

      // Wait for R3Chat's imperative handle to attach (it mounts in the same commit as this effect on
      // a cold load). Ref attachment lands within a frame or two; poll a bounded number of frames.
      const applyToChat = () => {
        if (chatRef.current) {
          // turn: if a turn is still running server-side, R3Chat reattaches to its live stream.
          chatRef.current.loadSession(sessionId, data.messages || [], contextData ?? undefined, data.turn ?? null)
          return true
        }
        return false
      }
      if (!applyToChat()) {
        let tries = 0
        const MAX_TRIES = 300 // ~5s at 60fps. On a cold load R3Chat can't mount until PostHog flags
        // load (the access provider holds the shell), so this must outlast a slow snapshot fetch, not just the
        // usual 1-2 frames of ref attachment. Bounded so a dead mount can't hold the loader forever.
        await new Promise<void>((resolve) => {
          const tick = () => {
            if (applyToChat() || ++tries >= MAX_TRIES) { resolve(); return }
            requestAnimationFrame(tick)
          }
          requestAnimationFrame(tick)
        })
      }
      setLiveSessionId(sessionId)
      return 'applied'
    } catch {
      // Session resume failed
      return 'aborted'
    } finally {
      setSessionResuming(false)
      setInitialChatLoadPending(false)
    }
  }, [authFetch, auth.token])

  const handleNewChat = useCallback(() => {
    chatRef.current?.reset()
    setLiveSessionId(undefined)
    setMcpPanelData(null)
    setMcpPanelOpen(false)
    // Also clear the site-builder panel — without this, opening a new chat while a website
    // panel is open leaves it stuck open (and toggleable with the previous chat's siteArtifacts).
    setSiteArtifacts(null)
    setSiteBuilderOpen(false)
    setMarkdownArtifact(null)
    setMarkdownArtifactOpen(false)
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMicroAppDefinitionId(null)
    setMicroAppDisplayName(null)
    setMicroAppPanelOpen(false)
    setViewingVersion(null)
    setSelectedArtifactId(null)
    setSelectedVersion(null)
    setPublishing(false)
    setSessionArtifacts([])
    setDraftBanner(null)
  }, [])

  // ── MCP App panel ─────────────────────────────────────────────────
  const [mcpPanelData, setMcpPanelData] = useState<McpAppData | null>(null)
  const [mcpPanelOpen, setMcpPanelOpen] = useState(false)
  const mcpContainerRef = useRef<McpAppContainerHandle>(null)

  const handleMcpApp = useCallback((data: McpAppData) => {
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setMcpPanelData(data)
    setMcpPanelOpen(true)
    setMarkdownArtifactOpen(false)
    if (data.sessionId) setLiveSessionId(prev => prev ?? data.sessionId)
  }, [])

  // ── Site builder panel ────────────────────────────────────────────
  const [siteArtifacts, setSiteArtifacts] = useState<SiteArtifacts | null>(null)
  const [siteBuilderOpen, setSiteBuilderOpen] = useState(false)
  const [markdownArtifact, setMarkdownArtifact] = useState<MarkdownArtifactPanelData | null>(null)
  const [markdownArtifactOpen, setMarkdownArtifactOpen] = useState(false)
  const [artifactsListOpen, setArtifactsListOpen] = useState(false)
  const [previewArtifact, setPreviewArtifact] = useState<SessionArtifact | null>(null)

  const handleMarkdownArtifact = useCallback((data: MarkdownArtifactPanelData) => {
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMcpPanelOpen(false)
    setSiteBuilderOpen(false)
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setMarkdownArtifact(data)
    setMarkdownArtifactOpen(true)
    setLiveSessionId(previous => previous ?? data.sessionId)
  }, [])

  // ── Micro-app panel (Cap'n Web dock beside chat) ───────────────────
  const [microAppDefinitionId, setMicroAppDefinitionId] = useState<string | null>(null)
  const [microAppDisplayName, setMicroAppDisplayName] = useState<string | null>(null)
  const [microAppPanelOpen, setMicroAppPanelOpen] = useState(false)

  const closeMicroAppPanel = useCallback(() => {
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setMicroAppDisplayName(null)
  }, [])

  /** Open a micro-app beside chat. Closes competing right panels. Does not auto-Accept (B1). */
  const openMicroApp = useCallback((definitionId: string, displayName?: string) => {
    if (!showExtensions) return
    const id = definitionId.trim()
    if (!id) return
    setMcpPanelOpen(false)
    setSiteBuilderOpen(false)
    setMarkdownArtifactOpen(false)
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMicroAppDefinitionId(id)
    setMicroAppDisplayName(displayName?.trim() || null)
    setMicroAppPanelOpen(true)
    // Ensure chat + panel are visible (finder lives on /extensions; agent open happens on /chat).
    navigate(liveSessionId ? `/chat/${liveSessionId}` : '/chat')
  }, [navigate, liveSessionId, showExtensions])

  useEffect(() => {
    if (!showExtensions) closeMicroAppPanel()
  }, [showExtensions, closeMicroAppPanel])

  // Whether ANY artifact panel is open — the panel-type-AGNOSTIC signal. Today that's the MCP panel
  // (crosstabs / survey) or the site-builder panel; when new producer panels land, OR them in HERE,
  // once. Every consumer (the artifact-list load effect + the turn-end refresh) keys off this instead
  // of enumerating panel types inline, so a new producer's panel is handled by construction — no
  // forgotten call site. Legacy micro-apps are not session artifacts. App Artifact uploads join
  // this same catalog and use their format renderer inside the shared artifact preview panel.
  const anyArtifactPanelOpen = mcpPanelOpen || siteBuilderOpen || markdownArtifactOpen || artifactsListOpen || !!previewArtifact

  const [sessionArtifacts, setSessionArtifacts] = useState<SessionArtifact[]>([])
  // How many artifacts each session owns — fetched once globally so the rail, Chats page, and the
  // Add-chat picker can show a "has artifacts" indicator without each re-fetching the whole catalog.
  const [artifactCountBySession, setArtifactCountBySession] = useState<Record<string, number>>({})
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null)
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [publishing, setPublishing] = useState(false)
  const publishingRef = useRef(false)
  useEffect(() => { publishingRef.current = publishing }, [publishing])

  const liveSessionIdRef = useRef(liveSessionId)
  useEffect(() => { liveSessionIdRef.current = liveSessionId }, [liveSessionId])

  const downloadArtifactVersion = useCallback(async (artifact: SessionArtifact, version: PanelArtifactVersion) => {
    if (!version.bodyR2Key) return
    try {
      const headers: Record<string, string> = {}
      if (auth.token) headers.Authorization = `Bearer ${auth.token}`
      const response = await authFetch(`${config.API_CF_URL}/api/r3/artifact-body?key=${encodeURIComponent(version.bodyR2Key)}`, {
        credentials: 'include', headers,
      })
      if (!response.ok) return
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = `${artifact.title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}-v${version.version}${typeDef(artifact.type).ext}`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    } catch { /* best-effort */ }
  }, [authFetch, auth.token])

  const deleteSessionArtifact = useCallback(async (artifact: SessionArtifact) => {
    setSessionArtifacts(current => current.filter(candidate => candidate.id !== artifact.id))
    setPreviewArtifact(current => current?.id === artifact.id ? null : current)
    try { await deleteArtifact(config.API_CF_URL, auth.token, artifact.id) }
    catch { /* best-effort; the next catalog refresh restores server truth */ }
  }, [auth.token])

  const loadArtifactCatalog = useCallback(async (): Promise<SessionArtifact[]> => {
    const headers: Record<string, string> = {}
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    try {
      const res = await authFetch(`${config.API_CF_URL}/api/r3/artifacts`, { credentials: 'include', headers, cache: 'no-store' })
      if (!res.ok) return []
      const data = await res.json() as { groups?: Array<{ items: SessionArtifact[] }> }
      return (data.groups ?? []).flatMap(g => g.items)
    } catch {
      return []
    }
  }, [authFetch, auth.token])

  const loadSessionArtifacts = useCallback(async (sessionId: string): Promise<SessionArtifact[]> => {
    const artifacts = await loadArtifactCatalog()
    return artifacts.filter(it => it.sessionId === sessionId)
  }, [loadArtifactCatalog])

  const handleAppArtifactOpen = useCallback(async (data: AppArtifactOpenData) => {
    const artifact = (await loadArtifactCatalog()).find(candidate => candidate.id === data.artifactId)
    if (!artifact || artifact.type !== 'app-artifact') return
    setMcpPanelOpen(false)
    setSiteBuilderOpen(false)
    setMarkdownArtifactOpen(false)
    setArtifactsListOpen(false)
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setMicroAppDisplayName(null)
    setSessionArtifacts(current => [artifact, ...current.filter(candidate => candidate.id !== artifact.id)])
    setSelectedArtifactId(artifact.id)
    setPreviewArtifact(artifact)
  }, [loadArtifactCatalog])

  const afterArtifactPublished = useCallback(async () => {
    const sid = liveSessionIdRef.current
    if (!sid) return
    const arts = await loadSessionArtifacts(sid)
    setSessionArtifacts(arts)
    if (arts.length > 0) {
      setSelectedArtifactId(prev => {
        if (prev !== null && arts.some(a => a.id === prev)) return prev
        return arts.reduce((a, b) => (latestPublishedAt(b) > latestPublishedAt(a) ? b : a)).id
      })
    }
  }, [loadSessionArtifacts])

  const handleSiteBuilderPlan = useCallback((data: SiteBuilderPlanData) => {
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setSiteArtifacts(prev => ({
      siteId: data.siteId,
      agentState: prev?.agentState ?? '',
      sitePlan: data.sections,
      htmlContent: prev?.htmlContent ?? null,
      renderHtml: prev?.renderHtml,
      pendingChangeSet: prev?.pendingChangeSet,
      clientSlug: prev?.clientSlug,
      publishedUrl: prev?.publishedUrl,
    }))
    setMarkdownArtifactOpen(false)
    setSiteBuilderOpen(true)
  }, [])

  const handleSiteBuilderGenerating = useCallback((siteId: string | number) => {
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setSiteBuilderOpen(true)
    setMarkdownArtifactOpen(false)
    setSiteArtifacts(prev => prev ? { ...prev, siteId } : { siteId, agentState: '', sitePlan: null, htmlContent: null })
  }, [])

  const handleSiteBuilderCodeChunk = useCallback((_data: SiteBuilderCodeChunkData) => {
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setSiteBuilderOpen(true)
    setMarkdownArtifactOpen(false)
  }, [])

  const handleSiteBuilderPreview = useCallback((renderHtml: string, siteId: string | number, revision?: number, htmlContent?: string) => {
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setSiteArtifacts(prev => ({
      siteId,
      agentState: prev?.agentState ?? '',
      sitePlan: prev?.sitePlan ?? null,
      htmlContent: htmlContent ?? renderHtml,
      renderHtml,
      pendingChangeSet: null,
      revision: revision ?? prev?.revision,
      clientSlug: prev?.clientSlug,
      publishedUrl: prev?.publishedUrl,
    }))
    setSiteBuilderOpen(true)
    setMarkdownArtifactOpen(false)
  }, [])

  const handleSiteBuilderPublished = useCallback((url: string, siteId: string | number) => {
    setSiteArtifacts(prev => prev ? { ...prev, siteId, publishedUrl: url } : prev)
    afterArtifactPublished()
  }, [afterArtifactPublished])

  // ── Right panel resize ────────────────────────────────────────────
  const [panelWidthPct, setPanelWidthPct] = useState(55)
  const isDraggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)

  // ── Panel view controls ────────────────────────────────────────────
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [siteView, setSiteView] = useState<'preview' | 'code'>('preview')

  const openSiteInNewTab = useCallback(() => {
    const html = siteArtifacts?.renderHtml ?? siteArtifacts?.htmlContent
    openSandboxedWebsitePreview(html)
  }, [siteArtifacts])

  const downloadSiteHtml = useCallback(() => {
    const html = siteArtifacts?.htmlContent
    if (!html) return
    const slug = siteArtifacts?.publishedUrl
      ? new URL(siteArtifacts.publishedUrl).hostname.split('.')[0]
      : 'deliverable'
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slug}.html`
    a.click()
    URL.revokeObjectURL(url)
  }, [siteArtifacts])

  const handleDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    isDraggingRef.current = true
    setIsDragging(true)
    const onMove = (ev: PointerEvent) => {
      if (!isDraggingRef.current) return
      const pct = 100 - (ev.clientX / window.innerWidth) * 100
      setPanelWidthPct(Math.max(25, Math.min(75, pct)))
    }
    const onUp = () => {
      isDraggingRef.current = false
      setIsDragging(false)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [])

  // ── MCP App resume on session select ──────────────────────────────
  const resourceUri = config.MCP_APP_URL || 'https://reachy-mcp-app.pages.dev'

  const handleSessionResumeMcp = useCallback(async (sessionId: string): Promise<boolean> => {
    if (!authoringEnabled) return false
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
      const res = await authFetch(`${config.API_CF_URL}/api/mcp/tool`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ serverId: 'authoring', tool: 'show_survey_review', sessionId }),
      }, {silentForbidden: true})
      if (!res.ok) return false
      const result = await res.json() as { structuredContent: Record<string, unknown> }
      const sc = result.structuredContent ?? {}
      if ((sc as Record<string, unknown[]>).nodes?.length > 0) {
        setMcpPanelData({
          serverId: 'authoring', resourceUri,
          toolName: 'show_survey_review', toolInput: {},
          toolResult: { content: [{ type: 'text', text: 'Restored' }], structuredContent: sc },
        })
        setMcpPanelOpen(true)
        return true
      }
    } catch { /* MCP resume is best-effort */ }
    return false
  }, [authFetch, auth.token, resourceUri, authoringEnabled])

  const handleSessionResumeCrosstabs = useCallback(async (sessionId: string): Promise<boolean> => {
    if (!crosstabsEnabled) return false
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
      const res = await authFetch(`${config.API_CF_URL}/api/crosstabs/tool`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ tool: 'get_state', sessionId, arguments: {} }),
      }, {silentForbidden: true})
      if (!res.ok) return false
      const result = await res.json() as { structuredContent?: Record<string, unknown> }
      const sc = result.structuredContent ?? {}
      if (!sc.classification) return false
      const jobComplete = (sc.latestJob as { status?: string } | null)?.status === 'complete'
      const activeTab = jobComplete ? 'preview'
        : sc.plan ? 'plan'
        : sc.objectives ? 'objectives'
        : 'variables'
      setMcpPanelData({
        serverId: 'crosstabs', resourceUri,
        toolName: 'show_crosstabs_review', toolInput: { tab: activeTab },
        toolResult: { content: [{ type: 'text', text: 'Restored' }], structuredContent: { ...sc, activeTab } },
      })
      setMcpPanelOpen(true)
      return true
    } catch { /* Crosstabs resume is best-effort */ }
    return false
  }, [authFetch, auth.token, resourceUri, crosstabsEnabled])

  const handleSessionResumeSiteBuilder = useCallback(async (sessionId: string): Promise<boolean> => {
    if (!websiteBuilderEnabled) return false
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
      const res = await authFetch(`${config.API_CF_URL}/api/site-builder/tool`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ tool: 'get_site', arguments: {}, sessionId }),
      }, {silentForbidden: true})
      if (!res.ok) return false
      const data = await res.json() as { structuredContent?: Record<string, unknown> }
      const sc = data.structuredContent
      if (!sc) return false
      const htmlContent = sc.htmlContent as string | null
      const renderHtml = (sc.renderHtml as string | null) ?? htmlContent
      const sitePlan = sc.sitePlan as SiteBuilderPlanData['sections'] | null
      if (renderHtml || sitePlan) {
        setSiteArtifacts({
          siteId: (sc.siteId as string | number) || 0,
          revision: Number(sc.revision ?? 0),
          agentState: (sc.agentState as string) || '',
          sitePlan: sitePlan || null,
          htmlContent: htmlContent || null,
          renderHtml: renderHtml || null,
          pendingChangeSet: (sc.pendingChangeSet as SiteArtifacts['pendingChangeSet']) ?? null,
          publishedUrl: (sc.publishedUrl as string) || undefined,
        })
        setSiteBuilderOpen(true)
        return true
      }
    } catch { /* Site builder resume is best-effort */ }
    return false
  }, [authFetch, auth.token, websiteBuilderEnabled])

  const handleSessionResumeMarkdown = useCallback(async (sessionId: string): Promise<boolean> => {
    if (!artifactsMgmtEnabled) return false
    try {
      const headers: Record<string, string> = {}
      if (auth.token) headers.Authorization = `Bearer ${auth.token}`
      const response = await authFetch(`${config.API_CF_URL}/api/r3/markdown-artifact?sessionId=${encodeURIComponent(sessionId)}`, {
        credentials: 'include', headers, cache: 'no-store',
      }, {silentForbidden: true})
      if (!response.ok) return false
      handleMarkdownArtifact(await response.json() as MarkdownArtifactPanelData)
      return true
    } catch {
      return false
    }
  }, [authFetch, auth.token, handleMarkdownArtifact, artifactsMgmtEnabled])

  const fetchDraftMeta = useCallback(async (sessionId: string, panelType?: string): Promise<{ artifactId?: string; baseVersion?: number; dirty?: boolean } | null> => {
    const endpoint = panelType === 'crosstabs' ? '/api/crosstabs/tool'
      : panelType === 'site_builder' ? '/api/site-builder/tool'
      : panelType === 'survey_review' ? '/api/mcp/tool' : null
    if (!endpoint) return null
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
      const res = await authFetch(`${config.API_CF_URL}${endpoint}`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({
          ...(panelType === 'survey_review' ? {serverId: 'authoring'} : {}),
          tool: 'get_draft_meta', sessionId, arguments: {},
        }),
      }, {silentForbidden: true})
      if (!res.ok) return null
      return ((await res.json()) as { structuredContent?: { artifactId?: string; baseVersion?: number; dirty?: boolean } }).structuredContent ?? null
    } catch {
      return null
    }
  }, [authFetch, auth.token])

  const refreshDraftBanner = useCallback(async (sessionId: string, panelType?: string) => {
    setDraftBanner(null)
    const meta = await fetchDraftMeta(sessionId, panelType)
    const dirty = !!(meta?.dirty && meta.artifactId)
    if (dirty) setDraftBanner({ type: panelType!, baseVersion: meta!.baseVersion ?? null })
  }, [fetchDraftMeta])

  const [viewingVersion, setViewingVersion] = useState<string | null>(null)
  const openArtifactVersion = useCallback(async (v: { id: string; type: string; version: number; title: string; sessionId: string | null; bodyR2Key?: string }): Promise<boolean> => {
    if (!v.bodyR2Key || !v.sessionId) return false
    try {
      const headers: Record<string, string> = {}
      if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
      const res = await authFetch(`${config.API_CF_URL}/api/r3/artifact-body?key=${encodeURIComponent(v.bodyR2Key)}`, { credentials: 'include', headers })
      if (!res.ok) return false
      const body = await res.json() as Record<string, any>
      setDraftBanner(null)
      setViewingVersion(`${v.title} · v${v.version}`)
      setSelectedArtifactId(v.id)
      setSelectedVersion(v.version)
      setLiveSessionId(v.sessionId)
      if (v.type === 'website') {
        setMcpPanelData(null)
        setSiteArtifacts({ siteId: body.snapshot?.siteId ?? 0, revision: body.sourceRevision ?? undefined, agentState: '', sitePlan: body.sitePlan ?? null, htmlContent: body.html ?? null, publishedUrl: body.publishedUrl ?? undefined })
        setSiteBuilderOpen(true)
        setMcpPanelOpen(false)
        return true
      }
      setSiteArtifacts(null); setSiteBuilderOpen(false)
      if (v.type === 'survey') {
        setMcpPanelData({
          serverId: 'authoring', resourceUri,
          toolName: 'show_survey_review', toolInput: {},
          toolResult: { content: [{ type: 'text', text: 'Published version' }], structuredContent: { ...body, studyName: `${v.title} (v${v.version})` } },
        })
        setMcpPanelOpen(true)
        return true
      }
      setMcpPanelData({
        serverId: 'crosstabs', resourceUri,
        toolName: 'show_crosstabs_review', toolInput: { tab: 'preview' },
        toolResult: { content: [{ type: 'text', text: 'Published version' }], structuredContent: {
          activeTab: 'preview',
          frozenOutputKey: body.outputR2Key, frozenExcelKey: body.excelR2Key ?? null,
          manifest: body.manifest ?? [], snapshot: body.snapshot ?? null,
        } },
      })
      setMcpPanelOpen(true)
      return true
    } catch { /* best-effort */ }
    return false
  }, [authFetch, auth.token, resourceUri])

  const openDraft = useCallback(async (type: string, artifactId: string | null) => {
    setViewingVersion(null)
    setSelectedVersion(null)
    setSelectedArtifactId(artifactId)
    if (!liveSessionId) return
    if (type === 'crosstabs') await handleSessionResumeCrosstabs(liveSessionId)
    else if (type === 'survey') await handleSessionResumeMcp(liveSessionId)
    else if (type === 'website') await handleSessionResumeSiteBuilder(liveSessionId)
    void refreshDraftBanner(liveSessionId, artifactTypeToPanelType(type))
  }, [liveSessionId, handleSessionResumeCrosstabs, handleSessionResumeMcp, handleSessionResumeSiteBuilder, refreshDraftBanner])

  const selectVersion = useCallback(async (artifact: SessionArtifact, version: PanelArtifactVersion) => {
    await openArtifactVersion({ ...version, id: artifact.id })
  }, [openArtifactVersion])

  useEffect(() => {
    if (!anyArtifactPanelOpen || !liveSessionId) { setSessionArtifacts([]); return }
    let cancelled = false
    loadSessionArtifacts(liveSessionId).then(arts => { if (!cancelled) setSessionArtifacts(arts) })
    return () => { cancelled = true }
  }, [anyArtifactPanelOpen, liveSessionId, loadSessionArtifacts])

  // Global "artifacts per session" tally for the has-artifacts indicators. One catalog fetch, grouped
  // by sessionId. Refreshes when auth is ready and after a publish (sessionArtifacts changing for the
  // live session is a cheap signal that the catalog moved).
  const refreshArtifactCounts = useCallback(async () => {
    const headers: Record<string, string> = {}
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    try {
      const res = await authFetch(`${config.API_CF_URL}/api/r3/artifacts`, { credentials: 'include', headers })
      if (!res.ok) return
      const data = await res.json() as { groups?: Array<{ items: SessionArtifact[] }> }
      const counts: Record<string, number> = {}
      for (const it of (data.groups ?? []).flatMap(g => g.items)) {
        const sid = it.sessionId
        if (sid) counts[sid] = (counts[sid] ?? 0) + 1
      }
      setArtifactCountBySession(counts)
    } catch { /* indicator is best-effort */ }
  }, [authFetch, auth.token])

  useEffect(() => { if (auth.ready) void refreshArtifactCounts() }, [auth.ready, refreshArtifactCounts, sessionArtifacts])

  // ── User profile settings ──────────────────────────────────────────────────
  const refreshUserSettings = useCallback(async () => {
    try { setUserSettings(await fetchUserSettings(config.API_CF_URL, auth.token)) }
    catch { /* settings stay null; UI falls back to JWT name + initials */ }
    finally { setUserSettingsLoaded(true) }
  }, [authFetch, auth.token])

  useEffect(() => { if (auth.ready) void refreshUserSettings() }, [auth.ready, refreshUserSettings])

  const saveUserSettings = useCallback(async (patch: UserSettingsPatch) => {
    const next = await patchUserSettings(config.API_CF_URL, auth.token, patch)
    // The PATCH response doesn't re-mint the signed avatarUrl (that's a GET-only concern), so it comes
    // back null even when the image avatar is unchanged. Preserve the current avatarUrl so a profile
    // edit doesn't visually blank the avatar until the next reload.
    setUserSettings(prev => (prev && next.avatarType === 'image' && !next.avatarUrl)
      ? { ...next, avatarUrl: prev.avatarUrl }
      : next)
    return next
  }, [authFetch, auth.token])

  const uploadUserAvatar = useCallback(async (file: File) => {
    const { avatarUrl } = await uploadAvatar(config.API_CF_URL, auth.token, file)
    // The upload sets avatar_type='image' + the key server-side; reflect it locally with the fresh URL.
    setUserSettings(prev => prev ? { ...prev, avatarType: 'image', avatarUrl } : prev)
  }, [authFetch, auth.token])

  const applyLocationSettings = useCallback((next: UserSettings) => {
    // Location responses do not mint a fresh signed avatar URL; keep the one already displayed.
    setUserSettings(prev => prev?.avatarUrl && !next.avatarUrl ? { ...next, avatarUrl: prev.avatarUrl } : next)
  }, [])

  const beginLocationFlow = useCallback((source: 'chat' | 'settings', reason?: string) => {
    setLocationError(null)
    setLocationFlow({ source, stage: 'permission', reason, result: null, resolutionSource: 'browser' })
  }, [])

  const handleLocationRequest = useCallback((data: LocationRequestData) => {
    beginLocationFlow('chat', data.reason)
  }, [beginLocationFlow])

  const declineUserLocation = useCallback(async () => {
    setLocationBusy(true)
    setLocationError(null)
    try {
      const wasChatFlow = locationFlow?.source === 'chat'
      const next = await declineBrowserLocation(config.API_CF_URL, auth.token)
      applyLocationSettings(next)
      setLocationFlow(null)
      if (wasChatFlow) {
        const continuation = 'I chose not to share my browser location. Please continue using a less precise network location if available, and clearly label it as approximate.'
        if (chatStreamingRef.current) pendingLocationContinuationRef.current = continuation
        else window.setTimeout(() => chatRef.current?.sendMessage(continuation), 0)
      }
    } finally {
      setLocationBusy(false)
    }
  }, [applyLocationSettings, auth.token, locationFlow?.source])

  const findUserLocation = useCallback(async () => {
    if (!navigator.geolocation) throw new Error('Location sharing is not supported by this browser.')
    setLocationBusy(true)
    setLocationError(null)
    setLocationFlow(current => current ? { ...current, stage: 'resolving', result: null, resolutionSource: 'browser' } : current)
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 12_000,
          maximumAge: 10 * 60 * 1000,
        })
      })
      const result = await resolveBrowserLocation(
        config.API_CF_URL,
        auth.token,
        position.coords.latitude,
        position.coords.longitude,
      )
      setLocationFlow(current => current
        ? { ...current, stage: 'confirm', result, resolutionSource: 'browser' }
        : current)
    } catch (error) {
      const geolocationError = typeof error === 'object' && error !== null && 'code' in error
        ? error as GeolocationPositionError
        : null
      if (geolocationError?.code === 1) {
        const next = await declineBrowserLocation(config.API_CF_URL, auth.token)
        applyLocationSettings(next)
        setLocationFlow(current => current ? { ...current, stage: 'permission' } : current)
        throw new Error('Location was not shared. You can change this later in browser and Ros settings.')
      }
      setLocationFlow(current => current ? { ...current, stage: 'permission' } : current)
      if (error instanceof Error) throw error
      throw new Error('Could not get your location. Please try again.')
    } finally {
      setLocationBusy(false)
    }
  }, [applyLocationSettings, auth.token])

  const resolveTypedLocation = useCallback(async (value: string, source: 'chat' | 'settings' = 'settings') => {
    const manualLocation = value.trim()
    if (!manualLocation) {
      setLocationFlow(current => current
        ? { ...current, stage: 'manual', result: null, resolutionSource: 'manual' }
        : { source, stage: 'manual', result: null, resolutionSource: 'manual' })
      return
    }
    setLocationBusy(true)
    setLocationError(null)
    setLocationFlow(current => current
      ? { ...current, stage: 'resolving', result: null, resolutionSource: 'manual' }
      : { source, stage: 'resolving', result: null, resolutionSource: 'manual' })
    try {
      const result = await resolveManualLocation(config.API_CF_URL, auth.token, manualLocation)
      setLocationFlow(current => current
        ? { ...current, stage: 'confirm', result, resolutionSource: 'manual' }
        : { source, stage: 'confirm', result, resolutionSource: 'manual' })
    } catch (error) {
      setLocationFlow(current => current
        ? { ...current, stage: 'manual' }
        : { source, stage: 'manual', result: null, resolutionSource: 'manual' })
      setLocationError(error instanceof Error ? error.message : 'Could not resolve this location.')
      throw error
    } finally {
      setLocationBusy(false)
    }
  }, [auth.token])

  const confirmResolvedLocation = useCallback(async () => {
    if (!locationFlow?.result) return
    setLocationBusy(true)
    setLocationError(null)
    try {
      const next = await confirmUserLocation(
        config.API_CF_URL,
        auth.token,
        locationFlow.result,
        locationFlow.resolutionSource,
      )
      applyLocationSettings(next)
      const wasChatFlow = locationFlow.source === 'chat'
      const displayName = next.locationDisplayName || locationFlow.result.resolution.displayName
      setLocationFlow(null)
      if (wasChatFlow) {
        const continuation = `I confirmed and saved my location as ${displayName}. Please continue my previous request using this confirmed location.`
        if (chatStreamingRef.current) pendingLocationContinuationRef.current = continuation
        else window.setTimeout(() => chatRef.current?.sendMessage(continuation), 0)
      }
    } catch (error) {
      setLocationError(error instanceof Error ? error.message : 'Could not save this location.')
    } finally {
      setLocationBusy(false)
    }
  }, [applyLocationSettings, auth.token, locationFlow])

  const removeSavedLocation = useCallback(async () => {
    const next = await removeUserLocation(config.API_CF_URL, auth.token)
    applyLocationSettings(next)
  }, [applyLocationSettings, auth.token])

  const handleSessionSelect = useCallback(async (sessionId: string, requestedArtifactId?: string) => {
    setMcpPanelData(null)
    setMcpPanelOpen(false)
    setSiteArtifacts(null)
    setSiteBuilderOpen(false)
    setMarkdownArtifact(null)
    setMarkdownArtifactOpen(false)
    setArtifactsListOpen(false)
    setPreviewArtifact(null)
    setMicroAppPanelOpen(false)
    setMicroAppDefinitionId(null)
    setMicroAppDisplayName(null)
    setDraftBanner(null)
    setViewingVersion(null)
    setSelectedArtifactId(null)
    setSelectedVersion(null)
    setPublishing(false)

    await handleSelectSession(sessionId)

    const artifacts = await loadSessionArtifacts(sessionId)
    setSessionArtifacts(artifacts)
    const newest = artifacts.length
      ? artifacts.reduce((a, b) => (latestPublishedAt(b) > latestPublishedAt(a) ? b : a))
      : null
    setSelectedArtifactId(newest?.id ?? null)
    const newestType = newest
      ? artifactTypeToPanelType(newest.type)
      : sessions.find(s => s.id === sessionId)?.panelType

    // An explicit artifact request always opens that artifact, regardless of artifact type or the
    // separate artifact-management rollout. This is the stable contract used by every library card.
    const requested = requestedArtifactId
      ? artifacts.find(artifact => artifact.id === requestedArtifactId) ?? null
      : null
    if (requested) {
      setSelectedArtifactId(requested.id)
      setPreviewArtifact(requested)
      return
    }

    // Formats without a legacy authoring panel (App Artifacts today, future artifact formats later)
    // restore through the shared preview panel from the persisted chat relationship alone. This is
    // what makes /chat/:id durable after the one-time artifactId navigation hint is gone.
    if (newest && !newestType) {
      setPreviewArtifact(newest)
      return
    }

    if (artifactsMgmtEnabled) {
      if (newestType === 'site_builder') {
        await handleSessionResumeSiteBuilder(sessionId)
        void refreshDraftBanner(sessionId, 'site_builder')
        return
      }
      if (artifacts.length > 0) {
        setArtifactsListOpen(true)
        return
      }
    }

    switch (newestType) {
      case 'markdown_artifact': await handleSessionResumeMarkdown(sessionId); return
      case 'crosstabs': await handleSessionResumeCrosstabs(sessionId); void refreshDraftBanner(sessionId, newestType); return
      case 'survey_review': await handleSessionResumeMcp(sessionId); void refreshDraftBanner(sessionId, newestType); return
      case 'site_builder': await handleSessionResumeSiteBuilder(sessionId); void refreshDraftBanner(sessionId, newestType); return
    }
    if (await handleSessionResumeCrosstabs(sessionId)) { void refreshDraftBanner(sessionId, 'crosstabs'); return }
    if (await handleSessionResumeMcp(sessionId)) { void refreshDraftBanner(sessionId, 'survey_review'); return }
    if (await handleSessionResumeSiteBuilder(sessionId)) { void refreshDraftBanner(sessionId, 'site_builder'); return }
    await handleSessionResumeMarkdown(sessionId)
  }, [sessions, handleSelectSession, loadSessionArtifacts, handleSessionResumeCrosstabs, handleSessionResumeMcp, handleSessionResumeSiteBuilder, handleSessionResumeMarkdown, refreshDraftBanner, artifactsMgmtEnabled])

  // ── File upload ───────────────────────────────────────────────────
  const uploadFile = useCallback(async (sessionId: string, file: File, signal?: AbortSignal): Promise<string> => {
    const headers: Record<string, string> = {}
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    const res = await fetch(
      `${config.API_CF_URL}/api/upload?sessionId=${encodeURIComponent(sessionId)}&fileName=${encodeURIComponent(file.name)}`,
      { method: 'POST', headers, body: file, credentials: 'include', signal },
    )
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
    const data = await res.json() as { key: string }
    return data.key
  }, [authFetch, auth.token])

  // ── Source context (back link) ────────────────────────────────────
  const source = new URLSearchParams(window.location.search).get('source')
  const sourceInfo = source ? KNOWN_SOURCES[source] : null
  void sourceInfo // (header back-link wiring handled by the rail/header; kept for parity)

  const fetchVoiceToken = useCallback(async (): Promise<string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    const res = await authFetch(`${config.API_CF_URL}/api/r3/transcribe/token`, {
      method: 'POST', credentials: 'include', headers,
    })
    if (!res.ok) throw new Error('Failed to get voice token')
    const data = await res.json() as { token: string }
    return data.token
  }, [authFetch, auth.token])

  const handleSessionCreated = useCallback((sessionId: string) => {
    setLiveSessionId(sessionId)
    // If this chat was started from inside a project, associate it now that it has a real id, then
    // clear the one-shot. reloadSessions picks up the new projectId annotation for the header chip.
    const projectId = pendingProjectRef.current
    pendingProjectRef.current = null
    if (projectId) {
      addChatToProject(config.API_CF_URL, auth.token, projectId, sessionId)
        .then(() => setTimeout(loadSessions, 1000))
        .catch(() => { /* association failed — chat still exists, just not linked */ })
    } else {
      setTimeout(loadSessions, 1000)
    }
  }, [loadSessions, auth.token])

  const onStreamingAssistantContent = useCallback((content: string | null) => {
    if (content === null) {
      // End of an assistant turn — the agent may have published or updated an artifact. Reload the
      // artifact list so an open panel reflects it. Keyed off the panel-agnostic anyArtifactPanelOpen
      // so EVERY producer's panel (crosstabs, website, and future ones) is covered — enumerating
      // panel types here was the refresh-gap bug.
      if (anyArtifactPanelOpen) afterArtifactPublished()
      // A turn ended, so the sessions list is now stale: a brand-new chat needs to appear, and an
      // existing chat that just got activity needs to jump to the top (backend sorts by lastActiveAt).
      // Reloading here makes the rail + Chats page reflect it live, no manual refresh.
      void loadSessions()
      // Dynamic naming is deliberately background work. A second quiet refresh picks up the new
      // contextual title without delaying the answer or requiring the user to reload the page.
      setTimeout(loadSessions, 2500)
      setPublishing(false)
    }
  }, [anyArtifactPanelOpen, afterArtifactPublished, loadSessions])

  // ── Cross-route entry points (for rail + Home) ─────────────────────
  // Navigate to a session: reflect in the URL; the URL-sync effect runs handleSessionSelect.
  const selectSession = useCallback((sessionId: string, artifactId?: string) => {
    navigate(artifactId
      ? `/chat/${sessionId}?artifactId=${encodeURIComponent(artifactId)}`
      : `/chat/${sessionId}`)
  }, [navigate])

  const newChat = useCallback(() => {
    handleNewChat()
    navigate('/chat')
  }, [handleNewChat, navigate])

  const openArtifactInChat = useCallback(async (artifact: SessionArtifact, sourceSessionId = artifact.sessionId) => {
    if (sourceSessionId) {
      selectSession(sourceSessionId, artifact.id)
      return
    }
    // A page-uploaded artifact has no source conversation. Persist a real chat first; selecting that
    // chat then follows the exact same history + artifact-panel restoration path as every other chat.
    const sessionId = await createArtifactChat(config.API_CF_URL, auth.token, artifact, authFetch as typeof fetch)
    void loadSessions()
    selectSession(sessionId, artifact.id)
  }, [auth.token, authFetch, loadSessions, selectSession])

  // Start a fresh chat that will auto-associate with the given project once its first message creates
  // the session (see handleSessionCreated). Opens a blank chat surface, same as newChat.
  const newChatInProject = useCallback((projectId: string) => {
    pendingProjectRef.current = projectId
    handleNewChat()
    navigate('/chat')
  }, [handleNewChat, navigate])

  // Start a fresh chat AND send the first message. R3Chat is always mounted, so chatRef is non-null
  // even when called from Home. handleNewChat clears panels first so no prior session leaks in.
  const startNewChatWithMessage = useCallback((text: string) => {
    const msg = text.trim()
    if (!msg) return
    handleNewChat()
    navigate('/chat')
    // sendMessage on the next tick so the workspace is visible + reset has applied.
    setTimeout(() => chatRef.current?.sendMessage(msg), 0)
  }, [handleNewChat, navigate])

  // Rename: optimistic local update + PATCH. On failure, reload to resync.
  const renameSession = useCallback(async (sessionId: string, name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, intentSummary: trimmed } : s))
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    try {
      const res = await authFetch(`${config.API_CF_URL}/api/r3/sessions/${sessionId}`, {
        method: 'PATCH', credentials: 'include', headers, body: JSON.stringify({ name: trimmed }),
      })
      if (!res.ok) loadSessions()
    } catch { loadSessions() }
  }, [authFetch, auth.token, loadSessions])

  const setSessionPinned = useCallback(async (sessionId: string, pinned: boolean) => {
    const pinnedAt = pinned ? Date.now() : undefined
    setSessions(prev => prev
      .map(session => session.id === sessionId ? { ...session, pinnedAt } : session)
      .sort((a, b) => {
        if (a.pinnedAt && !b.pinnedAt) return -1
        if (!a.pinnedAt && b.pinnedAt) return 1
        if (a.pinnedAt && b.pinnedAt) return b.pinnedAt - a.pinnedAt
        return (b.lastActiveAt ?? b.startedAt) - (a.lastActiveAt ?? a.startedAt)
      }))
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    try {
      const res = await authFetch(`${config.API_CF_URL}/api/r3/sessions/${sessionId}`, {
        method: 'PATCH', credentials: 'include', headers, body: JSON.stringify({ pinned }),
      })
      if (!res.ok) loadSessions()
    } catch { loadSessions() }
  }, [authFetch, auth.token, loadSessions])

  // Delete model (true Undo): the Chats page removes the row from the local list immediately and shows
  // an Undo toast. The actual server DELETE is DEFERRED — `deleteSession` is called only when the toast
  // window expires (commit). Undo just calls `restoreSessionLocally` and never calls this, so no data
  // is lost. We also reset to a safe view if the deleted chat was open.
  const deleteSession = useCallback(async (sessionId: string) => {
    if (liveSessionId === sessionId) { handleNewChat(); navigate('/') }
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`
    try {
      const res = await authFetch(`${config.API_CF_URL}/api/r3/sessions/${sessionId}`, {
        method: 'DELETE', credentials: 'include', headers,
      })
      if (!res.ok) loadSessions()  // server rejected — resync truth
    } catch { loadSessions() }
  }, [authFetch, auth.token, liveSessionId, handleNewChat, navigate, loadSessions])

  // Put a row back into the local list (Undo, before the deferred server delete fired).
  const restoreSessionLocally = useCallback((session: SessionSummary) => {
    setSessions(prev => prev.some(s => s.id === session.id) ? prev : [session, ...prev].sort((a, b) => (b.lastActiveAt ?? b.startedAt) - (a.lastActiveAt ?? a.startedAt)))
  }, [])

  // URL → session sync. The URL REFLECTS liveSessionId; the guard prevents a navigate() triggered by
  // handleSessionCreated/handleMcpApp from re-loading (which would wipe the just-created live chat).
  // The access provider mounts AppShell only after auth and the server snapshot are ready.
  const requestedArtifactId = new URLSearchParams(location.search).get('artifactId') ?? undefined
  useEffect(() => {
    if (urlSessionId && urlSessionId !== liveSessionId) {
      handleSessionSelect(urlSessionId, requestedArtifactId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSessionId, requestedArtifactId])

  // ── Cross-tab + refocus sync ────────────────────────────────────────
  // A chat open in two tabs went stale in the non-sending tab until a manual refresh. Two signals
  // fix that, both funneling into the same QUIET re-sync (refetch messages + turn status; R3Chat's
  // loadSession reattaches to a running turn's live stream — the reconnect machinery):
  //   1. BroadcastChannel: the sending tab's R3Chat announces "turn started on session X"
  //      (same-browser tabs; the sender never receives its own message).
  //   2. Tab refocus: when this tab becomes visible/focused again, re-sync the open session —
  //      covers activity that happened while the tab was in the background.
  // A busy flag collapses overlapping signals (broadcast + focus, rapid sends) into one loop.
  // expectChange (the broadcast path): a turn-started broadcast PROMISES new content, but the
  // receiving tab's fetch races the server write — the first read often still shows the old
  // history and no running turn. Retry a few times until the change becomes visible, instead of
  // skipping and leaving the tab stale until the next signal.
  const quietSyncBusyRef = useRef(false)
  const quietResync = useCallback(async (sessionId: string, opts?: { expectChange?: boolean }) => {
    if (chatStreamingRef.current) return  // this tab owns a live turn — never clobber it
    if (quietSyncBusyRef.current) return
    quietSyncBusyRef.current = true
    try {
      const MAX_TRIES = opts?.expectChange ? 5 : 1
      for (let i = 0; i < MAX_TRIES; i++) {
        const outcome = await handleSelectSession(sessionId, { quiet: true })
        if (outcome !== 'unchanged') return
        if (i < MAX_TRIES - 1) await new Promise(r => setTimeout(r, 700))
        if (chatStreamingRef.current || liveSessionIdRef.current !== sessionId) return
      }
    } finally {
      quietSyncBusyRef.current = false
    }
  }, [handleSelectSession])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const ch = new BroadcastChannel(ROS_TURN_CHANNEL)
    ch.onmessage = (e: MessageEvent) => {
      const { type, sessionId } = (e.data ?? {}) as { type?: string; sessionId?: string }
      if (type !== 'turn-started' || !sessionId) return
      void loadSessions()  // rail + Chats list freshness in every tab (new chat appears, active jumps up)
      if (sessionId === liveSessionIdRef.current) void quietResync(sessionId, { expectChange: true })
    }
    return () => ch.close()
  }, [quietResync, loadSessions])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const sid = liveSessionIdRef.current
      if (sid) void quietResync(sid)
      void loadSessions()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [quietResync, loadSessions])

  const shellValue = useMemo<RosShellValue>(() => ({
    sessions, sessionsLoading, sessionsHasMore, loadMoreSessions, reloadSessions: loadSessions,
    liveSessionId, chatHasMessages, artifactCountBySession, selectSession, openArtifactInChat, newChat, newChatInProject, startNewChatWithMessage,
    openMicroApp,
    renameSession, setSessionPinned, deleteSession, restoreSessionLocally,
    openHistory: () => setHistoryOpen(true),
    openAgents: () => setAgentsOpen(true),
    showAgents,
    openSettings: () => setSettingsOpen(true),
    userSettings, userSettingsLoaded, saveUserSettings, uploadUserAvatar,
    sidebarCollapsed: railCollapsed, setSidebarCollapsed,
    email: auth.email, name: auth.name, apiBaseUrl: config.API_CF_URL, token: auth.token,
    logout: auth.logout,
  }), [
    sessions, sessionsLoading, sessionsHasMore, loadMoreSessions, loadSessions,
    liveSessionId, chatHasMessages, artifactCountBySession, selectSession, openArtifactInChat, newChat, newChatInProject, startNewChatWithMessage,
    openMicroApp,
    renameSession, setSessionPinned, deleteSession, restoreSessionLocally, showAgents,
    userSettings, userSettingsLoaded, saveUserSettings, uploadUserAvatar,
    railCollapsed, setSidebarCollapsed, auth.email, auth.name, auth.token, auth.logout,
  ])

  // First name for greetings (chat empty-state + Home) — prefer the Settings preferred/full name over
  // the JWT name so "what should Ros call you?" drives the greeting. Hold off (undefined) until the
  // settings fetch resolves, so the name doesn't flicker (JWT/email flashing then snapping to Settings).
  const greetingFirstName = !userSettingsLoaded ? undefined : (
    (userSettings?.preferredName?.trim() || userSettings?.fullName?.trim() || auth.name || '').split(' ')[0] || undefined
  )

  if (!chatService) return null

  return (
    <>
    {/* Cold-load continuity overlay while history is attached to the always-mounted chat. */}
    {initialChatLoadPending && (
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    )}
    <RosShellContext.Provider value={shellValue}>
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
        {/* Persistent left rail */}
        <RosRail
          apiBaseUrl={config.API_CF_URL}
          token={auth.token}
          onOpenArtifactVersion={openArtifactVersion}
        />

        {/* Right of rail: the always-mounted chat workspace + the routed page overlay */}
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* ChatWorkspace is ALWAYS mounted. On /chat* it's visible; on other routes the Outlet
              page overlays it (workspace stays mounted underneath, preserving live chat state). */}
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', visibility: inChat ? 'visible' : 'hidden' }}>
            <ChatWorkspace
              chatRef={chatRef}
              chatService={chatService}
              userName={greetingFirstName}
              liveSessionId={liveSessionId}
              apiBaseUrl={config.API_CF_URL}
              token={auth.token}
              devMode={devMode}
              setDevMode={setDevMode}
              showDevToggle={showDevToggle}
              sessionResuming={sessionResuming && !initialChatLoadPending}
              onHasMessagesChange={setChatHasMessages}
              onMessagesMirror={handleMessagesMirror}
              onStreamingChange={(isStreaming) => {
                chatStreamingRef.current = isStreaming
                if (!isStreaming && pendingLocationContinuationRef.current) {
                  const continuation = pendingLocationContinuationRef.current
                  pendingLocationContinuationRef.current = null
                  window.setTimeout(() => chatRef.current?.sendMessage(continuation), 0)
                }
              }}
              onSessionCreated={handleSessionCreated}
              fetchVoiceToken={fetchVoiceToken}
              uploadFile={uploadFile}
              onStreamingAssistantContent={onStreamingAssistantContent}
              onMcpApp={handleMcpApp}
              onMarkdownArtifact={handleMarkdownArtifact}
              onAppArtifactOpen={handleAppArtifactOpen}
              onMicroAppOpen={showExtensions ? openMicroApp : undefined}
              onLocationRequest={showLocationTool ? handleLocationRequest : undefined}
              onSiteBuilderPlan={handleSiteBuilderPlan}
              onSiteBuilderGenerating={handleSiteBuilderGenerating}
              onSiteBuilderCodeChunk={handleSiteBuilderCodeChunk}
              onSiteBuilderPreview={handleSiteBuilderPreview}
              onSiteBuilderPublished={handleSiteBuilderPublished}
              draftBanner={draftBanner}
              setDraftBanner={() => setDraftBanner(null)}
              mcpPanelData={mcpPanelData}
              mcpPanelOpen={mcpPanelOpen}
              setMcpPanelOpen={setMcpPanelOpen}
              mcpContainerRef={mcpContainerRef}
              siteArtifacts={siteArtifacts}
              siteBuilderOpen={siteBuilderOpen}
              setSiteBuilderOpen={setSiteBuilderOpen}
              markdownArtifact={markdownArtifact}
              markdownArtifactOpen={markdownArtifactOpen}
              setMarkdownArtifactOpen={setMarkdownArtifactOpen}
              setMarkdownArtifact={setMarkdownArtifact}
              artifactsMgmtEnabled={artifactsMgmtEnabled}
              artifactsListOpen={artifactsListOpen}
              setArtifactsListOpen={setArtifactsListOpen}
              previewArtifact={previewArtifact}
              setPreviewArtifact={setPreviewArtifact}
              downloadArtifactVersion={downloadArtifactVersion}
              onDeleteArtifact={deleteSessionArtifact}
              microAppDefinitionId={microAppDefinitionId}
              microAppDisplayName={microAppDisplayName}
              microAppPanelOpen={showExtensions && microAppPanelOpen}
              setMicroAppPanelOpen={setMicroAppPanelOpen}
              closeMicroAppPanel={closeMicroAppPanel}
              sessionArtifacts={sessionArtifacts}
              selectedArtifactId={selectedArtifactId}
              selectedVersion={selectedVersion}
              viewingVersion={viewingVersion}
              setSelectedVersion={setSelectedVersion}
              setViewingVersion={setViewingVersion}
              openDraft={openDraft}
              selectVersion={selectVersion}
              publishing={publishing}
              setPublishing={setPublishing}
              panelWidthPct={panelWidthPct}
              isDragging={isDragging}
              handleDragStart={handleDragStart}
              previewDevice={previewDevice}
              setPreviewDevice={setPreviewDevice}
              siteView={siteView}
              setSiteView={setSiteView}
              openSiteInNewTab={openSiteInNewTab}
              downloadSiteHtml={downloadSiteHtml}
            />
          </Box>

          {/* Routed pages (Home, Chats, placeholders). Empty element on /chat* so the workspace shows. */}
          {!inChat && (
            <Box sx={{ position: 'absolute', inset: 0, overflow: 'auto', bgcolor: 'background.default' }}>
              <Outlet />
            </Box>
          )}
        </Box>

        {/* Overlays */}
        <SessionsOverlay
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          sessions={sessions}
          hasMore={sessionsHasMore}
          onLoadMore={loadMoreSessions}
          onSelectSession={selectSession}
          onOpenArtifactVersion={openArtifactVersion}
          token={auth.token}
        />
        {agentsOpen && showAgents && (
          <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, overflowY: 'auto', bgcolor: 'background.default' }}>
            <AgentsScreen
              apiBaseUrl={config.API_CF_URL}
              token={auth.token}
              onClose={() => setAgentsOpen(false)}
            />
          </Box>
        )}
        {settingsOpen && (
          <SettingsOverlay
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            settings={userSettings}
            onSave={saveUserSettings}
            onUploadAvatar={uploadUserAvatar}
            onFindLocation={() => beginLocationFlow('settings')}
            onResolveManualLocation={value => resolveTypedLocation(value, 'settings')}
            onRemoveLocation={removeSavedLocation}
            showAgents={showAgents}
            showRegistry={showRegistry}
            showLocation={showLocationTool}
            apiBaseUrl={config.API_CF_URL}
            token={auth.token}
          />
        )}
        <LocationConsentDialog
          open={showLocationTool && !!locationFlow}
          stage={locationFlow?.stage ?? 'permission'}
          busy={locationBusy}
          error={locationError}
          reason={locationFlow?.reason}
          result={locationFlow?.result ?? null}
          onFind={() => {
            void findUserLocation().catch(error => {
              setLocationError(error instanceof Error ? error.message : 'Could not share location. Please try again.')
            })
          }}
          onConfirm={() => { void confirmResolvedLocation() }}
          onManual={value => {
            void resolveTypedLocation(value, locationFlow?.source ?? 'settings').catch(() => undefined)
          }}
          onDecline={() => {
            void declineUserLocation().catch(() => setLocationError('Could not save your choice. Please try again.'))
          }}
          onCancel={() => {
            setLocationError(null)
            setLocationFlow(null)
          }}
        />
      </Box>
    </RosShellContext.Provider>
    </>
  )
}
